import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PaperPlaneTilt, UploadSimple } from '@phosphor-icons/react';

const API_BASE = window.location.origin;

export const DistributorFundRequestDialog = ({
  dialogOpen, setDialogOpen, resetForm,
  formData, setFormData,
  PAYMENT_MODES, BANK_LIST, ONLINE_TRANSFER_ACCOUNTS,
  handleSubmitFundRequest, submitting,
  handleScreenshot, uploading,
}) => (
  <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
    <DialogTrigger asChild>
      <Button className="bg-emerald-600 hover:bg-emerald-700 self-start sm:self-auto" data-testid="add-fund-request-btn">
        <PaperPlaneTilt size={18} className="mr-2" /> Fund Request
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg">Fund Add Request</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmitFundRequest} className="space-y-4 mt-2">
        {/* Amount + Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-medium">Amount <span className="text-red-500">*</span></Label>
            <Input type="number" step="0.01" min="1" value={formData.amount} onChange={e => setFormData(p => ({ ...p, amount: e.target.value }))} placeholder="Enter amount" required data-testid="fund-request-amount" />
          </div>
          <div>
            <Label className="text-sm font-medium">Deposit Date</Label>
            <Input type="date" value={formData.deposit_date} onChange={e => setFormData(p => ({ ...p, deposit_date: e.target.value }))} data-testid="fund-request-date" />
          </div>
        </div>

        {/* Payment Mode */}
        <div>
          <Label className="text-sm font-medium mb-2 block">Payment Mode <span className="text-red-500">*</span></Label>
          <div className="grid grid-cols-2 gap-2">
            {PAYMENT_MODES.map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => setFormData(p => ({ ...p, payment_mode: mode, bank_name: '', account_info: '' }))}
                className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all text-center ${
                  formData.payment_mode === mode
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
                data-testid={`payment-mode-${mode.toLowerCase().replace(/\s/g, '-')}`}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {/* Bank Selection - for CDM & Branch Deposit */}
        {(formData.payment_mode === 'CDM' || formData.payment_mode === 'Branch Deposit') && (
          <div>
            <Label className="text-sm font-medium">Select Bank <span className="text-red-500">*</span></Label>
            <select
              value={formData.bank_name}
              onChange={e => setFormData(p => ({ ...p, bank_name: e.target.value }))}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 mt-1 focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none"
              data-testid="fund-request-bank"
            >
              <option value="">-- Select Bank --</option>
              {BANK_LIST.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        )}

        {/* Account Selection - for Online Transfer */}
        {formData.payment_mode === 'Online Transfer' && (
          <div>
            <Label className="text-sm font-medium mb-2 block">Transfer to Account <span className="text-red-500">*</span></Label>
            <div className="space-y-2">
              {ONLINE_TRANSFER_ACCOUNTS.map(acc => (
                <button
                  key={acc}
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, account_info: acc }))}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                    formData.account_info === acc
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid={`account-${acc.replace(/[\s*]/g, '-').toLowerCase()}`}
                >
                  {acc}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* QR Code info */}
        {formData.payment_mode === 'QR Code' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-800">QR Code se payment karein</p>
            <p className="text-xs text-blue-600 mt-1">Payment karne ke baad reference number aur receipt upload karein.</p>
          </div>
        )}

        {/* Reference Number */}
        {formData.payment_mode && (
          <div>
            <Label className="text-sm font-medium">Reference / Transaction Number</Label>
            <Input
              value={formData.reference_number}
              onChange={e => setFormData(p => ({ ...p, reference_number: e.target.value }))}
              placeholder="UTR, Transaction ID, Receipt No."
              data-testid="fund-request-reference"
            />
          </div>
        )}

        {/* Receipt Upload */}
        {formData.payment_mode && (
          <div>
            <Label className="text-sm font-medium">Payment Receipt / Screenshot</Label>
            <div className="mt-1">
              {formData.screenshot_url ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                  <img src={`${API_BASE}${formData.screenshot_url}`} alt="Receipt" className="w-16 h-16 object-cover rounded border" />
                  <div className="flex-1">
                    <p className="text-sm text-green-600 font-medium">Receipt uploaded</p>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData(p => ({ ...p, screenshot_url: '' }))} className="text-red-500 text-xs mt-1 h-auto p-0">
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshot}
                    disabled={uploading}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="fund-request-screenshot"
                  />
                  <div className="flex items-center gap-2 border-2 border-dashed border-gray-200 rounded-lg p-3 text-center hover:border-gray-300 cursor-pointer">
                    <UploadSimple size={20} className="text-gray-400" />
                    <span className="text-sm text-gray-500">{uploading ? 'Uploading...' : 'Upload receipt image'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <Label className="text-sm font-medium">Notes (Optional)</Label>
          <Input value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional details" data-testid="fund-request-notes" />
        </div>

        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" disabled={submitting} data-testid="submit-fund-request-btn">
          {submitting ? 'Submitting...' : 'Submit Fund Request'}
        </Button>
      </form>
    </DialogContent>
  </Dialog>
);
